import { useEffect, useState } from "react";
import { Building2, ChevronDown, Check } from "lucide-react";
import { api, getActiveOperator, setActiveOperator, OPERATOR_EVENT, OPERATORS_LIST_CHANGED } from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Top-bar operator switcher.
 * - Fetches /operators (returns only the ones the user can access).
 * - Auto-selects when the user has exactly one; forces a pick when >1.
 * - Persists selection in localStorage and broadcasts a global event so all
 *   currently-mounted pages can refresh their data.
 */
export default function OperatorSwitcher({ testidSuffix = "" }) {
  const { user } = useAuth();
  const [operators, setOperators] = useState([]);
  const [activeId, setActiveIdState] = useState(getActiveOperator());
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    const doFetch = () => api.get("/operators").then((r) => {
      if (!alive) return;
      setOperators(r.data || []);
      setLoaded(true);
      const currentValid = (r.data || []).some((o) => o.id === getActiveOperator());
      if (!currentValid) {
        if ((r.data || []).length === 1) {
          const only = r.data[0].id;
          setActiveOperator(only);
          setActiveIdState(only);
        } else if (getActiveOperator()) {
          setActiveOperator(null);
          setActiveIdState(null);
        }
      }
    }).catch(() => { setLoaded(true); });
    doFetch();
    const h = () => doFetch();
    window.addEventListener(OPERATORS_LIST_CHANGED, h);
    return () => { alive = false; window.removeEventListener(OPERATORS_LIST_CHANGED, h); };
  }, [user?.id]);

  const active = operators.find((o) => o.id === activeId);
  const pick = (id) => {
    setActiveOperator(id);
    setActiveIdState(id);
    setOpen(false);
    // Force refresh so all pages re-query with new header
    window.location.reload();
  };

  useEffect(() => {
    const h = (e) => setActiveIdState(e.detail);
    window.addEventListener(OPERATOR_EVENT, h);
    return () => window.removeEventListener(OPERATOR_EVENT, h);
  }, []);

  if (!loaded) return null;
  // Admin without any operators created yet: guide them to the panel
  if (operators.length === 0) {
    return (
      <div className="flex items-center gap-2 text-xs" style={{ color: "var(--text-mute)" }}>
        <Building2 size={14} />
        {user?.role === "admin"
          ? <a href="/operadoras" className="underline" style={{ color: "var(--primary)" }} data-testid="op-empty-cta">Cadastrar operadora</a>
          : <span>Sem operadora atribuída</span>}
      </div>
    );
  }

  return (
    <div className="relative" data-testid={`operator-switcher${testidSuffix}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="btn btn-ghost flex items-center gap-2"
        data-testid={`operator-switcher-btn${testidSuffix}`}
        title="Alternar operadora"
      >
        <span
          className="inline-block"
          style={{
            width: 10, height: 10, borderRadius: 999,
            background: active?.color || "var(--primary)",
          }}
        />
        <span className="text-xs font-semibold" style={{ letterSpacing: "0.05em" }}>
          {active?.name || "Selecionar operadora"}
        </span>
        <ChevronDown size={14} />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 z-50 shadow-2xl"
          style={{ width: 240, background: "var(--surface)", border: "1px solid var(--border-strong)" }}
          data-testid={`operator-switcher-menu${testidSuffix}`}
        >
          <div className="px-3 py-2 label-caps border-b" style={{ borderColor: "var(--border)" }}>
            Suas operadoras
          </div>
          {operators.map((o) => (
            <button
              key={o.id}
              onClick={() => pick(o.id)}
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm"
              style={{
                background: o.id === activeId ? "rgba(56,189,248,0.08)" : "transparent",
                borderBottom: "1px solid var(--border)",
              }}
              data-testid={`operator-option-${o.id}`}
            >
              <span
                style={{ width: 10, height: 10, borderRadius: 999, background: o.color || "var(--primary)" }}
              />
              <span className="flex-1">{o.name}</span>
              {o.id === activeId && <Check size={14} style={{ color: "var(--primary)" }} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
